import { useState } from "react";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useApi, useResource, errorText } from "@/lib/api";
import RecordDialog from "@/components/RecordDialog";
import ConfirmDelete from "@/components/ConfirmDelete";

// Generic setup table: list, add, edit, toggle active, delete — all writes are audited server-side.
export default function CrudTab({ table, singular, prefix, fields, columns, blank, onChange, header }) {
  const api = useApi();
  const { data, error, reload } = useResource(`/${table}`);
  const [editing, setEditing] = useState(null);

  const refresh = async () => { await reload(); onChange?.(); };
  const payload = (row) => Object.fromEntries(fields.map((f) => [f.key, row[f.key]]));

  const save = async (form) => {
    try {
      if (editing.id) await api("put", `/${table}/${editing.id}`, payload(form));
      else await api("post", `/${table}`, payload(form));
      toast.success(`${singular} saved`);
      setEditing(null);
      await refresh();
    } catch (e) { return errorText(e); }
  };
  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); await refresh(); } catch (e) { toast.error(errorText(e)); }
  };

  return (
    <section className="space-y-5" data-testid={`${prefix}-section`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        {header}
        <Button onClick={() => setEditing({ ...blank })} data-testid={`add-${prefix}-button`} className="ml-auto">
          <Plus className="h-4 w-4 mr-1" /> Add {singular.toLowerCase()}
        </Button>
      </div>
      {error && <p className="text-destructive text-sm">{error}</p>}
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid={`${prefix}-table`}>
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              {columns.map((c) => <th key={c.key} className={`px-5 py-3 font-medium ${c.right ? "text-right" : ""}`}>{c.label}</th>)}
              <th className="px-5 py-3 font-medium">Active</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody>
            {(data || []).map((row) => (
              <tr key={row.id} className={`border-b border-border last:border-0 hover:bg-accent/40 transition-colors ${row.active ? "" : "opacity-50"}`} data-testid={`${prefix}-row-${row.id}`}>
                {columns.map((c) => <td key={c.key} className={`px-5 py-3 ${c.right ? "text-right" : ""}`}>{c.render ? c.render(row) : row[c.key]}</td>)}
                <td className="px-5 py-3">
                  <Switch checked={row.active} data-testid={`${prefix}-active-toggle-${row.id}`}
                    onCheckedChange={(v) => run(() => api("put", `/${table}/${row.id}`, { ...payload(row), active: v }), `${row.name} ${v ? "activated" : "deactivated"}`)} />
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing(row)} data-testid={`${prefix}-edit-${row.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <ConfirmDelete name={row.name} testId={`${prefix}-delete-${row.id}`}
                    onConfirm={() => run(() => api("delete", `/${table}/${row.id}`), `${row.name} deleted`)} />
                </td>
              </tr>
            ))}
            {data && data.length === 0 && (
              <tr><td colSpan={columns.length + 2} className="px-5 py-8 text-center text-muted-foreground">Nothing here yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <RecordDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} prefix={`${prefix}-form`}
        title={editing?.id ? `Edit ${singular.toLowerCase()}` : `Add ${singular.toLowerCase()}`}
        fields={fields} initial={editing || blank} onSubmit={save} />
    </section>
  );
}
